import React, { useEffect, useState, useCallback } from 'react';
import { CartItem, SupportedCurrency } from '../../types';
import { useUIStore } from '../../stores/useUIStore';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useCartStore, DiscountInfo } from '../../stores/useCartStore';
import { cn } from '../../utils/cn';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

export interface AppliedDiscount {
  code: string;
  discount_pct: number;
  discount_amount?: number;
  message?: string;
}

export interface CartDrawerProps {
  /** Optional override for controlled visibility (defaults to useUIStore.isCartOpen) */
  isOpen?: boolean;
  /** Close callback (defaults to useUIStore.closeCart) */
  onClose?: () => void;
  /** Optional callback when user proceeds to checkout */
  onProceedToCheckout?: () => void;
  /** Optional items override (if not directly bound to useCartStore) */
  items?: CartItem[];
  /** Optional clear cart handler */
  onClearCart?: () => void;
  /** Optional quantity update handler */
  onUpdateQuantity?: (offerId: string, quantity: number) => void;
  /** Optional item removal handler */
  onRemoveItem?: (offerId: string) => void;
  /** Optional applied coupon override */
  appliedDiscount?: AppliedDiscount | DiscountInfo | null;
  /** Optional coupon apply handler */
  onApplyCoupon?: (code: string) => Promise<boolean>;
  /** Optional coupon remove handler */
  onRemoveCoupon?: () => void;
  /** Optional slot or component for FriendshipValidator */
  renderFriendshipValidator?: React.ReactNode;
}

/**
 * 1:1 Pixel-Perfect Replica of the original production elGordoFortnite Cart Drawer
 * Based directly on `wt` in `frontend/assets/tienda-tld5QqMl.js`.
 */
export const CartDrawer: React.FC<CartDrawerProps> = ({
  isOpen: propIsOpen,
  onClose: propOnClose,
  onProceedToCheckout: _onProceedToCheckout,
  items: propItems,
  onClearCart: _propOnClearCart,
  onUpdateQuantity: _propOnUpdateQuantity,
  onRemoveItem: propOnRemoveItem,
}) => {
  // Store Subscriptions
  const storeIsOpen = useUIStore((s) => s.isCartOpen);
  const storeCloseCart = useUIStore((s) => s.closeCart);
  const currency: SupportedCurrency = useCurrencyStore((s) => s.currency);
  const calculatePrice = useCurrencyStore((s) => s.calculatePrice);

  const isOpen = propIsOpen !== undefined ? propIsOpen : storeIsOpen;
  const handleCloseProp = propOnClose || storeCloseCart;

  // Cart Store Subscriptions
  const storeItems = useCartStore((s) => s.items);
  const storeRemoveItem = useCartStore((s) => s.removeItem);
  const items = propItems !== undefined ? propItems : storeItems;

  const [isClosing, setIsClosing] = useState(false);

  // Safe Close with exit animation (matching 200ms anim-drawer-out)
  const handleClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    unlockScroll('cart-drawer');
    setTimeout(() => {
      setIsClosing(false);
      handleCloseProp();
    }, 200);
  }, [isClosing, handleCloseProp]);

  // Escape Key Handler
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Body Scroll Lock & Modal Class
  useEffect(() => {
    if (isOpen) {
      lockScroll('cart-drawer');

      return () => {
        unlockScroll('cart-drawer');
      };
    }
  }, [isOpen]);

  const handleRemove = (offerId: string) => {
    if (propOnRemoveItem) {
      propOnRemoveItem(offerId);
    } else {
      storeRemoveItem(offerId);
    }
  };

  const handleProceed = () => {
    if (items.length === 0) return;
    handleClose();
    useUIStore.getState().navigateTo('cart', '/cart.html');
  };

  // Calculations matching original production `w` and `g` with quantity multipliers
  const totalItemCount = items.reduce((sum, it) => sum + (it.quantity || 1), 0);
  const totalVbucks = items.reduce((sum, it) => sum + (it.vbucks ?? it.finalPrice ?? 0) * (it.quantity || 1), 0);
  const totalFiat = items.reduce((sum, it) => {
    const v = it.vbucks ?? it.finalPrice ?? 0;
    return sum + calculatePrice(v, it.offerId || it.id) * (it.quantity || 1);
  }, 0);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Carrito de compras"
      onClick={handleClose}
      className={cn(
        'fixed inset-0 z-[60] flex justify-end soft-backdrop overscroll-contain select-none',
        isClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
      )}
    >
      {/* Slide-in Drawer Container (Sin borde blanco lateral, con sombra fluida y animación física) */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'w-[88vw] max-w-[360px] sm:max-w-md bg-white cart-drawer-shadow',
          'flex flex-col h-full overscroll-contain text-[var(--g-ink)]',
          isClosing ? 'anim-drawer-out' : 'anim-drawer-in'
        )}
      >
        {/* 1. Header (Deep Blue con ícono blanco sobre corte GF, sin celeste estridente ni borde blanco) */}
        <div className="p-4 md:p-5 border-b border-[#0a2a9c]/20 flex justify-between items-center bg-[#0a2a9c] text-white shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 bg-white text-[#0a2a9c] flex items-center justify-center font-bold text-sm shrink-0 shadow-sm"
              style={{ clipPath: 'var(--g-cut)' }}
            >
              <i className="fa-solid fa-cart-shopping" />
            </div>
            <div>
              <h2 className="text-base md:text-lg font-black uppercase italic tracking-wider text-white font-chakra leading-tight">
                Tu Carrito
              </h2>
              <span className="text-xs text-blue-200 font-bold font-chakra">
                {totalItemCount} {totalItemCount === 1 ? 'objeto seleccionado' : 'objetos seleccionados'}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 bg-white/15 hover:bg-white text-white hover:text-[#06173f] flex items-center justify-center transition active:scale-95 cursor-pointer"
            style={{ clipPath: 'var(--g-slash)' }}
            title="Cerrar panel"
            aria-label="Cerrar panel"
          >
            <i className="fa-solid fa-xmark text-sm" />
          </button>
        </div>

        {/* 2. Scrollable Body Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-3 custom-scrollbar overscroll-contain bg-[var(--g-ice)]/35">
          {items.some(
            (it) =>
              it.offerId?.startsWith('recharge_') ||
              it.offerId === 'recharge_crew' ||
              it.offerId === 'recharge_vbucks_12500' ||
              (it as any).category === 'Recargas'
          ) && (
            <div
              className="bg-amber-50 border-2 border-amber-300 p-2.5 text-xs text-amber-900 font-medium flex items-center gap-2.5 mb-2"
              style={{ clipPath: 'var(--g-slash-sm)' }}
            >
              <i className="fa-brands fa-whatsapp text-emerald-600 text-lg shrink-0" />
              <span>
                <b className="font-heading uppercase">Coordinación por WhatsApp:</b> Tu carrito
                incluye recargas directas. La carga se coordina por WhatsApp tras realizar el pago.
              </span>
            </div>
          )}

          {items.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20 px-4 flex flex-col items-center justify-center h-full">
              <div
                className="w-16 h-16 bg-[var(--g-ice)] border-2 border-[var(--g-line)] flex items-center justify-center text-[var(--g-royal)] text-2xl mb-4 shadow-sm"
                style={{ clipPath: 'var(--g-cut)' }}
              >
                <i className="fa-solid fa-bag-shopping" />
              </div>
              <p className="font-black uppercase tracking-wider text-base text-[var(--g-ink)] font-chakra">
                El carrito está vacío
              </p>
              <p className="text-xs text-[var(--g-soft)] mt-1.5 max-w-[220px] font-barlow">
                Explora los objetos de la tienda y agrégalos para comprar
              </p>
            </div>
          ) : (
            /* Selected Items List */
            items.map((item) => {
              const itemVbucks = item.vbucks ?? item.finalPrice ?? 0;
              const itemQuantity = item.quantity || 1;
              const unitFiat = calculatePrice(itemVbucks, item.offerId || item.id);
              const totalLineVbucks = itemVbucks * itemQuantity;
              const totalLineFiat = unitFiat * itemQuantity;

              const isRechargeVbucks =
                item.offerId === 'recharge_vbucks_12500' ||
                item.offerId === 'recharge_crew' ||
                item.offerId?.startsWith('recharge_') ||
                item.image?.includes('cid_016_athena_commando_f') ||
                item.image?.includes('character_faintdoor');
              const itemImage = isRechargeVbucks
                ? 'https://fortnite-api.com/images/vbuck.png'
                : item.image || item.images?.icon || item.images?.featured || '';

              return (
                <div
                  key={item.offerId || item.id}
                  className="flex items-center gap-3 bg-white p-3 border-2 border-[var(--g-line)] hover:border-[var(--g-royal)] transition group"
                  style={{ clipPath: 'var(--g-slash)' }}
                >
                  {/* Item Image with Chamfered Cut */}
                  <img
                    loading="lazy"
                    decoding="async"
                    src={itemImage}
                    alt={item.name}
                    className={cn(
                      'w-14 h-14 bg-[var(--g-ice)] border border-[var(--g-line)] shrink-0 select-none pointer-events-none',
                      isRechargeVbucks ? 'object-contain p-1' : 'object-cover object-top'
                    )}
                    style={{ clipPath: 'var(--g-cut)' }}
                    onError={(e) => {
                      e.currentTarget.src = '/vbuck.png';
                    }}
                  />

                  {/* Title and V-Bucks Price */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-black uppercase italic text-xs md:text-sm truncate text-[var(--g-ink)] font-chakra leading-snug">
                        {item.name || 'Objeto'}
                      </h4>
                      {itemQuantity > 1 && (
                        <span className="px-1.5 py-0.5 bg-[#eff6ff] text-[#1447e6] border border-[#bfdbfe] text-[10px] font-black font-chakra shrink-0">
                          x{itemQuantity}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <span
                        className="w-4 h-4 bg-[#1447e6] text-white flex items-center justify-center text-[9px] font-black italic font-chakra shrink-0"
                        style={{ clipPath: 'var(--g-cut)' }}
                      >
                        V
                      </span>
                      <span className="font-black text-xs md:text-sm text-[var(--g-royal)] font-chakra">
                        {totalLineVbucks.toLocaleString('es-AR')} Pavos
                      </span>
                      {itemQuantity > 1 && (
                        <span className="text-[10px] text-[var(--g-soft)] font-chakra">
                          ({itemVbucks.toLocaleString('es-AR')} c/u)
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-bold text-[var(--g-ink)] mt-0.5 font-chakra">
                      ${totalLineFiat.toLocaleString('es-AR')} {currency}
                      {itemQuantity > 1 && (
                        <span className="text-[10px] text-[var(--g-soft)] font-normal ml-1">
                          (${unitFiat.toLocaleString('es-AR')} c/u)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Red Outline Trash Button */}
                  <button
                    type="button"
                    onClick={() => handleRemove(item.offerId || item.id || '')}
                    className="w-8 h-8 bg-white hover:bg-red-50 text-red-500 hover:text-red-600 border-2 border-red-200 hover:border-red-400 flex items-center justify-center transition shrink-0 active:scale-95 cursor-pointer"
                    style={{ clipPath: 'var(--g-slash)' }}
                    title="Eliminar del carrito"
                    aria-label="Eliminar del carrito"
                  >
                    <i className="fa-solid fa-trash-can text-xs" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* 3. Sticky Summary Footer */}
        <div className="p-4 md:p-5 border-t-2 border-[var(--g-line)] bg-white space-y-3 shrink-0">
          <div className="space-y-2">
            {/* Subtotal Pavos */}
            <div className="flex justify-between items-center">
              <span className="text-[var(--g-soft)] font-bold uppercase tracking-wider text-[11px] md:text-xs font-chakra">
                Subtotal Pavos
              </span>
              <div className="flex items-center gap-1.5">
                <span
                  className="w-4 h-4 bg-[#1447e6] text-white flex items-center justify-center text-[9px] font-black italic font-chakra shrink-0"
                  style={{ clipPath: 'var(--g-cut)' }}
                >
                  V
                </span>
                <span className="font-black text-sm md:text-base text-[var(--g-ink)] font-chakra">
                  {totalVbucks.toLocaleString('es-AR')} Pavos
                </span>
              </div>
            </div>

            {/* Subtotal (Currency) */}
            <div className="flex justify-between items-center pb-2 border-b-2 border-[var(--g-line)]">
              <span className="text-[var(--g-soft)] font-bold uppercase tracking-wider text-[11px] md:text-xs font-chakra">
                Subtotal ({currency})
              </span>
              <span className="font-black text-[var(--g-royal)] text-base md:text-lg font-chakra">
                ${totalFiat.toLocaleString('es-AR')} {currency}
              </span>
            </div>
          </div>

          {/* Action Button: IR A PAGAR */}
          <button
            type="button"
            onClick={handleProceed}
            disabled={items.length === 0}
            className={cn(
              'btn w-full py-3.5 font-chakra font-black uppercase italic tracking-wider text-xs md:text-sm',
              'transition-all shadow-md text-center flex items-center justify-center gap-2 cursor-pointer',
              items.length === 0
                ? 'opacity-50 pointer-events-none bg-[var(--g-line)] text-[var(--g-soft)]'
                : 'bg-[var(--g-royal)] hover:bg-[var(--g-deep)] text-white active:scale-95'
            )}
            style={{ clipPath: 'var(--g-slash)' }}
          >
            <span>Ir a Pagar</span>
            <i className="fa-solid fa-arrow-right text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
